import { useEffect, useState } from 'react'

import type { ComputedRecruitmentArea } from '../types'
import { getPrefectureColor } from '../utils'
import styles from './styles.module.scss'

export type CityGroup = {
  city: string
  areas: ComputedRecruitmentArea[]
  remaining: number
  percentage: number
}

type Props = {
  prefecture: string
  areas: ComputedRecruitmentArea[]
  cityGroups: CityGroup[]
  percentage: number
  activeAreaCount: number
  groupByCity: boolean
}

const priorityClass = { SS: 'ss', S: 's', A: 'a', B: 'b', C: 'done' } as const
const scrollToApplication = () =>
  document.getElementById('form')?.scrollIntoView({ behavior: 'smooth', block: 'start' })

const AreaRecruitmentPanel = ({
  prefecture,
  areas,
  cityGroups,
  percentage,
  activeAreaCount,
  groupByCity,
}: Props): JSX.Element => {
  const [openCities, setOpenCities] = useState<string[]>([])
  useEffect(
    () => setOpenCities(groupByCity && cityGroups.length ? [cityGroups[0].city] : []),
    [prefecture, groupByCity, cityGroups]
  )

  const renderArea = (area: ComputedRecruitmentArea) => (
    <div className={styles.areaRow} key={area.area}>
      <div className={styles.areaMain}>
        <div className={styles.areaName}>
          {area.area}
          <span className={styles.areaType}>{area.type}</span>
          <span className={`${styles.badge} ${styles[priorityClass[area.priority]]}`}>
            {area.status}
          </span>
        </div>
        <div className={styles.areaProgress} aria-hidden="true">
          <i style={{ width: `${area.percentage}%` }} />
        </div>
        <div className={styles.areaNumbers}>
          達成率 <b>{area.percentage}%</b>
          {area.status === '達成済み' ? '　達成済み' : ''}
        </div>
      </div>
      <span className={styles.areaCta}>
        {area.status === '達成済み' ? (
          <span className={styles.areaDone}>達成</span>
        ) : (
          <button type="button" onClick={scrollToApplication}>
            紹介する
          </button>
        )}
      </span>
    </div>
  )

  return (
    <div className={styles.panel}>
      <div className={styles.panelHead} aria-live="polite" aria-atomic="true">
        <div className={styles.panelPrefecture}>
          <span style={{ background: getPrefectureColor(areas) }} />
          {prefecture}
        </div>
        <div className={styles.panelMeta}>
          募集中 <b>{activeAreaCount}</b> エリア ／ 達成率 <b>{percentage}%</b>
        </div>
      </div>
      <div className={styles.panelBody}>
        {!areas.length ? (
          <div className={styles.panelEmpty}>
            <div>
              <p>このエリアは募集準備中です。ほかの都道府県もチェックしてみてください。</p>
            </div>
          </div>
        ) : groupByCity ? (
          cityGroups.map((group) => {
            const isOpen = openCities.includes(group.city)
            return (
              <div className={`${styles.cityGroup} ${isOpen ? styles.open : ''}`} key={group.city}>
                <button
                  className={styles.cityHead}
                  type="button"
                  aria-expanded={isOpen}
                  onClick={() =>
                    setOpenCities((current) =>
                      current.includes(group.city)
                        ? current.filter((city) => city !== group.city)
                        : [...current, group.city]
                    )
                  }
                >
                  <span className={styles.cityName}>{group.city}</span>
                  <span className={styles.cityMeta}>
                    {group.areas.length}エリア ／ 達成率 <b>{group.percentage}%</b>
                  </span>
                  <span className={styles.cityChevron} aria-hidden="true">
                    ⌄
                  </span>
                </button>
                {isOpen ? (
                  <div className={styles.cityAreas}>{group.areas.map(renderArea)}</div>
                ) : null}
              </div>
            )
          })
        ) : (
          areas.map(renderArea)
        )}
      </div>
    </div>
  )
}

export default AreaRecruitmentPanel
